import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    'bot-field': ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) setError('');
  };

  const encode = (data) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
      .join('&');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData['bot-field']) {
      setIsSuccess(true);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({
          'form-name': 'contact',
          ...formData
        })
      });
      
      setIsSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '', 'bot-field': '' });
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly at hello@alera.co.nz');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-24 pb-8 bg-gradient-to-br from-rose-50 via-beige-50 to-rose-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-playfair font-medium text-charcoal-900 mb-4">
              Get In Touch
            </h1>
            <p className="text-lg text-charcoal-600 max-w-2xl mx-auto leading-relaxed">
              Have questions about our wellness programs or want to share your success story? 
              We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-playfair font-medium text-charcoal-900 mb-6">
                Send Us a Message
              </h2>
              
              {isSuccess ? (
                <div className="bg-rose-50 border rounded-2xl p-8 text-center" style={{ borderColor: '#f0e0dc' }}>
                  <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg style={{ color: '#d9a79c' }} className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-charcoal-900 mb-2">Message Sent!</h3>
                  <p className="text-charcoal-600">
                    Thank you for reaching out. We'll get back to you within 48 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <p className="hidden">
                    <label>Don't fill this out if you're human: <input name="bot-field" value={formData['bot-field']} onChange={(e) => handleInputChange('bot-field', e.target.value)} /></label>
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-charcoal-700 mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        style={{ borderColor: '#f0e0dc' }}
                        className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:border-transparent"
                        onFocus={(e) => e.currentTarget.style.borderColor = '#d9a79c'}
                        onBlur={(e) => e.currentTarget.style.borderColor = '#f0e0dc'}
                        placeholder="Your full name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-charcoal-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        style={{ borderColor: '#f0e0dc' }}
                        className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:border-transparent"
                        onFocus={(e) => e.currentTarget.style.borderColor = '#d9a79c'}
                        onBlur={(e) => e.currentTarget.style.borderColor = '#f0e0dc'}
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">
                      Subject
                    </label>
                    <select
                      value={formData.subject}
                      onChange={(e) => handleInputChange('subject', e.target.value)}
                      className="w-full px-4 py-3 border border-sage-200 rounded-xl focus:ring-2 focus:ring-sage-500 focus:border-transparent"
                    >
                      <option value="">Select a topic</option>
                      <option value="general">General Question</option>
                      <option value="subscription">Subscription Support</option>
                      <option value="recipes">Recipe Questions</option>
                      <option value="eczema">Eczema & Skin Health</option>
                      <option value="fitness">Fitness & Movement</option>
                      <option value="partnership">Partnership Inquiry</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-charcoal-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      rows={6}
                      className="w-full px-4 py-3 border border-sage-200 rounded-xl focus:ring-2 focus:ring-sage-500 focus:border-transparent resize-none"
                      placeholder="Tell us how we can help you..."
                      required
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <p className="text-red-700">{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{ backgroundColor: '#d9a79c' }}
                    className="w-full text-white py-4 rounded-xl font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending Message...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Contact Info & FAQ */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-8"
            >
              {/* Social Links */}
              <div className="text-center">
                <h4 className="font-medium text-charcoal-900 mb-4">Follow Our Journey</h4>
                <div className="flex justify-center space-x-4">
                  <a href="https://www.instagram.com/alera.living" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center hover:bg-rose-200 transition-colors">
                    <svg style={{ color: '#d9a79c' }} className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                  <a href="https://www.facebook.com/profile.php?id=61584902072892" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center hover:bg-rose-200 transition-colors">
                    <svg style={{ color: '#d9a79c' }} className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                  <a href="https://www.tiktok.com/@alera.co.nz" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center hover:bg-rose-200 transition-colors">
                    <svg style={{ color: '#d9a79c' }} className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05 0-12.07z"/>
                    </svg>
                  </a>
                </div>
              </div>

              {/* FAQ Section */}
              <div className="bg-white rounded-3xl p-6 border border-sage-200">
                <h4 className="text-xl font-playfair font-medium text-charcoal-900 mb-4">Quick Questions</h4>
                <div className="space-y-2">
                  {/* FAQ 1 */}
                  <div className="border-b border-sage-100">
                    <button
                      onClick={() => toggleFaq(0)}
                      className="w-full py-3 flex items-center justify-between text-left hover:opacity-80 transition-opacity"
                    >
                      <span className="font-semibold text-charcoal-900">Do you offer refunds?</span>
                      <svg
                        className={`w-5 h-5 text-charcoal-600 transition-transform ${openFaq === 0 ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openFaq === 0 && (
                      <div className="pb-3">
                        <p className="text-sm text-charcoal-600">Monthly memberships can be cancelled anytime. Annual memberships are non-refundable once accessed. View our <RouterLink to="/refund-cancellation" style={{ color: '#d9a79c' }} className="underline">refund policy</RouterLink>.</p>
                      </div>
                    )}
                  </div>

                  {/* FAQ 2 */}
                  <div className="border-b border-sage-100">
                    <button
                      onClick={() => toggleFaq(1)}
                      className="w-full py-3 flex items-center justify-between text-left hover:opacity-80 transition-opacity"
                    >
                      <span className="font-semibold text-charcoal-900">Is everything personalised?</span>
                      <svg
                        className={`w-5 h-5 text-charcoal-600 transition-transform ${openFaq === 1 ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openFaq === 1 && (
                      <div className="pb-3">
                        <p className="text-sm text-charcoal-600">Our programs are expertly designed monthly workouts and recipes that work for everyone. They're not customized to individuals, but offer variety and modifications for all levels.</p>
                      </div>
                    )}
                  </div>

                  {/* FAQ 3 */}
                  <div className="border-b border-sage-100">
                    <button
                      onClick={() => toggleFaq(2)}
                      className="w-full py-3 flex items-center justify-between text-left hover:opacity-80 transition-opacity"
                    >
                      <span className="font-semibold text-charcoal-900">Can I join without allergies or eczema?</span>
                      <svg
                        className={`w-5 h-5 text-charcoal-600 transition-transform ${openFaq === 2 ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openFaq === 2 && (
                      <div className="pb-3">
                        <p className="text-sm text-charcoal-600">Absolutely! While we specialize in allergy-friendly wellness, anyone looking for supportive fitness and nutrition guidance is welcome in our community.</p>
                      </div>
                    )}
                  </div>

                  {/* FAQ 4 */}
                  <div className="border-b border-sage-100">
                    <button
                      onClick={() => toggleFaq(3)}
                      className="w-full py-3 flex items-center justify-between text-left hover:opacity-80 transition-opacity"
                    >
                      <span className="font-semibold text-charcoal-900">What to expect after I join?</span>
                      <svg
                        className={`w-5 h-5 text-charcoal-600 transition-transform ${openFaq === 3 ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openFaq === 3 && (
                      <div className="pb-3">
                        <p className="text-sm text-charcoal-600">You'll gain instant access to your member dashboard with monthly workouts, allergy-friendly recipes, community forum, goal tracking tools, and wellness resources.</p>
                      </div>
                    )}
                  </div>

                  {/* FAQ 5 */}
                  <div className="border-b border-sage-100">
                    <button
                      onClick={() => toggleFaq(4)}
                      className="w-full py-3 flex items-center justify-between text-left hover:opacity-80 transition-opacity"
                    >
                      <span className="font-semibold text-charcoal-900">I'm new to training. Will Alera support me?</span>
                      <svg
                        className={`w-5 h-5 text-charcoal-600 transition-transform ${openFaq === 4 ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openFaq === 4 && (
                      <div className="pb-3">
                        <p className="text-sm text-charcoal-600">Yes! We offer multiple program options including beginner-friendly workouts, exercise tutorials, and modifications for all fitness levels. Start where you are.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
